// DD-3742: a tab left open keeps running the bundle it loaded, long after a release. Once the
// backend drops a field that bundle still selects, its queries fail. Every build writes its id into
// the bundle (REACT_APP_BUILD_ID) and into /version.json (scripts/write-version-file.js); a tab that
// sees a different id on the server knows it is out of date and reloads.

const VERSION_FILE = '/version.json';
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;
const RELOADED_FOR_KEY = 'denthub.reloadedForBuildId';

// Empty in local dev and in builds without a commit ref: version checks are then off.
export const BUILD_ID = process.env.REACT_APP_BUILD_ID || '';

let newBuildId: string | undefined;
let lastCheckAt = 0;
let pendingCheck: Promise<string | undefined> | undefined;
const listeners = new Set<() => void>();

export const getNewBuildId = (): string | undefined => newBuildId;

export const subscribeToNewVersion = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const fetchServerBuildId = async (): Promise<string | undefined> => {
    try {
        const response = await fetch(VERSION_FILE, { cache: 'no-store' });
        if (!response.ok) return undefined;
        // Before the first deploy with this file, the SPA fallback answers with index.html and
        // json() throws: treated as "no version info".
        const body = await response.json();
        return typeof body?.buildId === 'string' && body.buildId ? body.buildId : undefined;
    } catch {
        return undefined;
    }
};

/** Resolves true once the server has a different build than this tab is running. */
export const checkForNewVersion = async (force = false): Promise<boolean> => {
    if (!BUILD_ID) return false;
    if (newBuildId) return true;
    if (!pendingCheck) {
        if (!force && Date.now() - lastCheckAt < MIN_CHECK_GAP_MS) return false;
        lastCheckAt = Date.now();
        pendingCheck = fetchServerBuildId().finally(() => {
            pendingCheck = undefined;
        });
    }
    const serverBuildId = await pendingCheck;
    if (serverBuildId && serverBuildId !== BUILD_ID && !newBuildId) {
        newBuildId = serverBuildId;
        listeners.forEach((listener) => listener());
    }
    return !!newBuildId;
};

/**
 * Reloads onto the new build without the user asking. At most once per new build per tab, so a
 * cache still serving the old bundle cannot cause a reload loop.
 */
export const autoReloadToNewVersion = (): boolean => {
    if (!newBuildId) return false;
    try {
        if (sessionStorage.getItem(RELOADED_FOR_KEY) === newBuildId) return false;
        sessionStorage.setItem(RELOADED_FOR_KEY, newBuildId);
    } catch {
        // Storage blocked: auto reloads only follow a navigation or a failed query, so no tight loop.
    }
    window.location.reload();
    return true;
};

/** Polls for a new build, and checks straight away when the user comes back to the tab. */
export const startVersionWatch = (): (() => void) => {
    if (!BUILD_ID) return () => undefined;

    const check = () => {
        checkForNewVersion();
    };
    const checkWhenVisible = () => {
        if (document.visibilityState === 'visible') check();
    };
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', checkWhenVisible);
    check();

    return () => {
        window.clearInterval(interval);
        window.removeEventListener('focus', check);
        document.removeEventListener('visibilitychange', checkWhenVisible);
    };
};
